// Team status: one shared feed for the whole app. While anything shows it (the Team panel in the header is always
// mounted), this tab sends a heartbeat and refreshes everyone's status once a minute, and again when the tab comes
// back into view. Rules live on the server (backend/presence.py).
import { useSyncExternalStore } from "react";
import api from "@/lib/api";

export const STATUS = {
  online: { label: "Online", dot: "bg-emerald-500" },
  in_meeting: { label: "In a meeting", dot: "bg-orange-500" },
  in_showroom: { label: "In showroom", dot: "bg-orange-500" },
  on_site: { label: "On site", dot: "bg-orange-500" },
  away: { label: "Away", dot: "bg-yellow-400" },
  offline: { label: "Offline", dot: "bg-gray-400" },
};
export const MY_CHOICES = [["", "Online (automatic)"], ["in_showroom", "In showroom"], ["in_meeting", "In a meeting"],
  ["on_site", "On site"], ["away", "Away"]];
const EVERY_MS = 60000;

let state = { rows: [], loaded: false };
const subs = new Set();
let timer = null;

const emit = () => subs.forEach((f) => f());
export function refreshPresence() {
  return api.get("/presence", { skipCache: true })
    .then(({ data }) => { state = { rows: Array.isArray(data) ? data : [], loaded: true }; emit(); })
    .catch(() => {});
}
const beat = () => (typeof document !== "undefined" && document.visibilityState === "hidden"
  ? Promise.resolve() : api.post("/presence/heartbeat").catch(() => {}));
const tick = () => beat().then(refreshPresence);
const onVisible = () => { if (document.visibilityState === "visible") tick(); };

function subscribe(fn) {
  subs.add(fn);
  if (!timer) {
    tick();
    timer = setInterval(tick, EVERY_MS);
    document.addEventListener("visibilitychange", onVisible);
  }
  return () => {
    subs.delete(fn);
    if (!subs.size && timer) {
      clearInterval(timer);
      timer = null;
      document.removeEventListener("visibilitychange", onVisible);
    }
  };
}

/** Everyone's status: [{user_id, name, status, label, last_seen, manual, me}], online first. */
export function usePresence() {
  return useSyncExternalStore(subscribe, () => state);
}

export async function setMyStatus(status) {
  await api.put("/presence/me", { status });
  await refreshPresence();
}

/** "seen 5 min ago" / "seen 3 h ago" / "seen 12 Oct" for someone offline. */
export function lastSeenText(iso, now = Date.now()) {
  const t = Date.parse(iso || "");
  if (!t) return "not seen yet";
  const min = Math.max(0, Math.round((now - t) / 60000));
  if (min < 60) return `seen ${min} min ago`;
  if (min < 24 * 60) return `seen ${Math.round(min / 60)} h ago`;
  return `seen ${new Date(t).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" })}`;
}

export function PresenceDot({ status, className = "" }) {
  const s = STATUS[status] || STATUS.offline;
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ring-2 ring-white ${s.dot} ${className}`} title={s.label} aria-label={s.label} />;
}
